// Cupón emitido y lo que se lleva el cliente: en el cierre del wizard y en la venta ya cerrada.
import type { ReactNode } from 'react';
import { Button } from '@/kit';
import { useWizardSession } from '@/domains/sales/wizard/api/useWizard';
import type { SlotSale } from '@/shared/patterns/SaleReceipt';
import { formatDateMx } from '@/lib/dateHelpers';
import { useSaleCouponWithPolling } from '../api/useWizardPromotions';
import type { SaleCoupon } from '../api/wizardPromotionsContract';
import type { SaleAudience } from '../lib/saleAudience';
import {
    benefitsCopy,
    type BenefitsCopy,
    type ClosingGift,
    closingGifts,
    couponDiscountText,
    entitlementText,
    saleGifts,
    saleWalletBonusesOf,
} from '../lib/saleClosing';
import { PromotionBonusItem } from '@/domains/sales/wizard/components/PromotionBonusItem';
import { EarnedCodeCard } from '@/shared/patterns/EarnedCode';
import { ClosedSaleGiftName } from './ClosedSaleGiftName';
import { SummaryGiftName } from './SummaryGiftName';

/** Cierre del wizard (slot `coupon` del resumen): los regalos salen de la sesión. */
export function SaleClosingBenefits({ sessionId, saleId }: { sessionId: string; saleId: string }) {
    const { data: session } = useWizardSession(sessionId);
    return (
        <BenefitsCard
            saleId={saleId}
            sessionId={sessionId}
            copy={benefitsCopy('CLOSING')}
            gifts={closingGifts(session?.reservedLines)}
            giftName={(gift) => (
                <SummaryGiftName sessionId={sessionId} sku={gift.sku} catalogName={gift.name} />
            )}
            couponCard={(coupon) => (
                <EarnedCodeCard
                    code={coupon.codeText}
                    benefit={nextPurchaseText(coupon)}
                    validUntil={formatDateMx(coupon.validUntil)}
                />
            )}
        />
    );
}

/** Venta ya cerrada (Mis ventas, comprobante, Admin VL): los regalos salen de sus líneas. */
export function ClosedSaleBenefits({
    sale,
    audience,
    retailerId,
    couponRetailerId,
}: {
    sale: SlotSale;
    audience: SaleAudience;
    retailerId?: string;
    /** Cadena con la que el admin de tenant lee el cupón. */
    couponRetailerId?: string;
}) {
    return (
        <BenefitsCard
            saleId={sale.saleId}
            audience={audience}
            couponRetailerId={couponRetailerId}
            copy={benefitsCopy('CLOSED_SALE')}
            gifts={saleGifts(sale.lines)}
            giftName={(gift) => (
                <ClosedSaleGiftName
                    saleId={sale.saleId}
                    audience={audience}
                    sku={gift.sku}
                    lineName={gift.name}
                    {...(retailerId !== undefined && { retailerId })}
                />
            )}
        />
    );
}

function BenefitsCard({
    saleId,
    sessionId,
    audience,
    couponRetailerId,
    copy,
    gifts,
    giftName,
    couponCard,
}: {
    saleId: string;
    sessionId?: string;
    audience?: SaleAudience;
    couponRetailerId?: string;
    copy: BenefitsCopy;
    gifts: ClosingGift[];
    /** Nombre del regalo; si la línea solo trae el SKU, lo resuelve Promociones. */
    giftName: (gift: ClosingGift) => ReactNode;
    /** Cómo se pinta el cupón; sin él, la línea compacta de la venta cerrada. */
    couponCard?: (coupon: SaleCoupon) => ReactNode;
}) {
    const { query: couponQuery, pollingDone } = useSaleCouponWithPolling(saleId, {
        ...(sessionId !== undefined && { sessionId }),
        ...(audience !== undefined && { audience }),
        ...(couponRetailerId !== undefined && { retailerId: couponRetailerId }),
    });
    const data = couponQuery.data;
    const items: { key: string; content: ReactNode }[] = [
        ...gifts.map((gift, i) => ({
            key: `gift-${String(i)}-${gift.sku}`,
            content: <>Regalo: {giftName(gift)}</>,
        })),
        ...(data?.entitlements ?? [])
            .map(entitlementText)
            .filter(Boolean)
            .map((text) => ({ key: text, content: text })),
    ];
    const walletBonuses = saleWalletBonusesOf(data?.entitlements ?? []);
    const generating = data?.availability === 'GENERATING';
    const coupon = data?.availability === 'READY' ? data.coupon : null;
    if (!generating && !coupon && items.length === 0 && walletBonuses.length === 0) return null;

    return (
        <section
            aria-label={copy.title}
            // En el cierre del wizard el cupón ya es su propia tarjeta: sin caja alrededor.
            className={
                couponCard
                    ? 'flex flex-col gap-2'
                    : 'flex flex-col gap-2 rounded-[10px] border border-border bg-muted/35 px-4 py-3.5'
            }
        >
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {copy.title}
            </div>
            {coupon &&
                (couponCard ? (
                    couponCard(coupon)
                ) : (
                    <CouponCard coupon={coupon} title={copy.couponTitle} />
                ))}
            {generating && (
                <div className="flex items-center justify-between gap-2 text-[12.5px] text-muted-foreground">
                    <span>{pollingDone ? copy.stillGenerating : copy.generating}</span>
                    {pollingDone && (
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={couponQuery.isFetching}
                            onClick={() => void couponQuery.refetch()}
                        >
                            Volver a consultar
                        </Button>
                    )}
                </div>
            )}
            {walletBonuses.length > 0 && (
                <ul aria-label="Bonos de la promo de venta" className="flex flex-col gap-2">
                    {walletBonuses.map((b) => (
                        <PromotionBonusItem
                            key={b.key}
                            kind="SALE"
                            label={b.label}
                            amountCents={b.amountCents}
                            displayStatus={b.displayStatus}
                        />
                    ))}
                </ul>
            )}
            {items.length > 0 && (
                <ul className="flex flex-col gap-0.5 text-[12.5px]">
                    {items.map((item) => (
                        <li key={item.key}>{item.content}</li>
                    ))}
                </ul>
            )}
        </section>
    );
}

/** «$300 de descuento en tu próxima compra»; sin monto, solo que sirve en la próxima. */
function nextPurchaseText(coupon: SaleCoupon): string {
    const discount = couponDiscountText(coupon.discount);
    return discount ? `${discount} en tu próxima compra` : 'Para tu próxima compra';
}

function couponValidityText(coupon: SaleCoupon): string {
    const discount = couponDiscountText(coupon.discount);
    const validUntil = formatDateMx(coupon.validUntil);
    return discount ? `${discount} · vence el ${validUntil}` : `Vence el ${validUntil}`;
}

/** Cupón en una sola línea: título, código y vigencia. */
function CouponCard({ coupon, title }: { coupon: SaleCoupon; title: string }) {
    return (
        <div className="flex flex-wrap items-baseline gap-x-1.5 rounded-md border border-success-soft-border bg-success-soft px-2 py-1 text-[12px]">
            <span className="text-muted-foreground">{title}</span>
            <span className="font-mono font-semibold">{coupon.codeText}</span>
            <span className="text-muted-foreground">· {couponValidityText(coupon)}</span>
        </div>
    );
}
